import { LayoutElementsResult } from '@src/utility/stackedLayout';
import { TranscriptElement } from '../../types.ts/transcript';
import { WORDS_TIMESTAMP_FLOAT_ERROR } from '../constants/karaokeConstants';
import { LayoutAwareTranscriptElement } from '../types/karaokeVariablePositionings';

// WIP: Abstraction of Creatomate format of a timeline clip.
export default class TimelineClip {
  track: number;
  time: number;
  duration: number;
  layoutResult: LayoutElementsResult;

  constructor(
    element: Record<string, any>,
    layoutResult: LayoutElementsResult,
  ) {
    this.track = element.track;
    this.time = element.time;
    this.duration = element.duration;
    this.layoutResult = layoutResult;
  }

  startTime() {
    return this.time;
  }

  endTime() {
    return this.startTime() + this.duration;
  }

  // unused
  getTextContent(transcriptElements: TranscriptElement[]): string {
    return transcriptElements
      .filter((element) =>
        this.isWithinTimeRange(element, this.time, this.duration),
      )
      .filter((element) => this.isValidElement(element))
      .map((element) => element.value)
      .join(' ');
  }

  getTranscriptElements(
    allTranscriptElements: TranscriptElement[],
  ): TranscriptElement[] {
    let started = false;
    const result: TranscriptElement[] = [];

    // Step through transcript
    // Remove all elements (incl punct) until we find a matching transcript time.
    // Then include everything
    // Then bail once we leave the clip.
    for (const element of allTranscriptElements) {
      if (
        element.state === 'cut' ||
        element.state === 'removed' ||
        this.isEmptyReplacement(element)
      ) {
        continue;
      }

      if (element.type === 'punct') {
        if (started) {
          result.push(element);
        }
        continue;
      }

      if (this.isWithinTimeRange(element, this.time, this.duration)) {
        if (!started) {
          started = true;
        }
        result.push(element);
      } else if (started) {
        break;
      }
    }

    this.markElementsAsLayoutAware(result);

    return result;
  }

  private markElementsAsLayoutAware(elements: TranscriptElement[]): void {
    // no need to check isLayoutChild as timeline clips shouldn't be based on
    // top/bottom children
    if (!this.layoutResult.isHiddenParent) {
      return;
    }
    for (const element of elements) {
      (element as LayoutAwareTranscriptElement)._layoutType =
        this.layoutResult.layoutType;
    }
  }

  // util to clear previously marked elements because we cannot rely on past calculation
  static unmarkElementsAsLayoutAware(elements: TranscriptElement[]): void {
    for (const element of elements) {
      (element as LayoutAwareTranscriptElement)._layoutType = undefined;
    }
  }

  private isEmptyReplacement(element: TranscriptElement): boolean {
    return element.state === 'replaced' && element.value === '';
  }

  private isWithinTimeRange(
    element: TranscriptElement,
    globalTime: number,
    duration: number,
  ): boolean {
    const startTime = globalTime;
    const endTime = globalTime + duration;
    const elementDuration = (element.end_ts ?? 0) - (element.ts ?? 0);

    // Calculate the overlapping segment
    const overlapStart = Math.max(element.ts ?? 0, startTime);
    const overlapEnd = Math.min(element.end_ts ?? 0, endTime);
    const overlapLength = Math.max(0, overlapEnd - overlapStart);

    // Check if the overlap is more than half of the duration of transcription element
    // or is fully within clip if element duration is zero
    return elementDuration > 0
      ? overlapLength > elementDuration / 2
      : (element.ts ?? 0) >= startTime - WORDS_TIMESTAMP_FLOAT_ERROR &&
          (element.end_ts ?? 0) <= endTime + WORDS_TIMESTAMP_FLOAT_ERROR;
  }

  private isValidElement(element: TranscriptElement): boolean {
    return Boolean(
      element.value && (element.type === 'text' || element.type === 'punct'),
    );
  }
}
